
import {
    Box,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";

import type { WidgetProps } from "../types";
import { formatCurrency0, formatInt, formatPercent, formatGeneratedAt } from "./format";

// Live-data widget #1: per-ASIN current marketplace state.
// v0.5: snapshot pre-rendered at provision time, stored in widget.data.
// v1: same component reads widget.data populated from a live fetcher.
export function OpportunitySnapshot({ widget }: WidgetProps<"opportunity_snapshot">) {
    const { props, data } = widget;
    const title = props.title ?? "Opportunity snapshot";
    const sorted = [...data.asins].sort(
        (a, b) => (b.modeledMonthlyRevenue ?? 0) - (a.modeledMonthlyRevenue ?? 0),
    );

    return (
        <Box>
            <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "flex-end" }}
                spacing={1}
                sx={{ mb: 2 }}
            >
                <Box>
                    <Typography variant="h4" sx={{ mb: 0.5 }}>
                        {title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Snapshot · {formatGeneratedAt(data.generatedAt)}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        textAlign: { xs: "left", sm: "right" },
                        p: 2,
                        border: 1,
                        borderColor: "divider",
                        bgcolor: "background.paper",
                        borderRadius: "10px",
                        minWidth: 200,
                    }}
                >
                    <Typography variant="overline" color="text.secondary">
                        Modeled monthly revenue
                    </Typography>
                    <Typography variant="h3" sx={{ color: "secondary.main", fontWeight: 700 }}>
                        {formatCurrency0(data.totalModeledRevenue)}
                    </Typography>
                </Box>
            </Stack>

            <TableContainer
                sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: "10px",
                    bgcolor: "background.paper",
                }}
            >
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: "primary.main" }}>
                            <TableCell sx={CELL_HEAD}>ASIN</TableCell>
                            <TableCell sx={CELL_HEAD}>Title</TableCell>
                            <TableCell sx={CELL_HEAD} align="right">
                                BSR
                            </TableCell>
                            <TableCell sx={CELL_HEAD} align="right">
                                Buy Box win rate (30d)
                            </TableCell>
                            <TableCell sx={CELL_HEAD} align="right">
                                Modeled monthly
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sorted.map((row) => (
                            <TableRow key={row.asin} hover>
                                <TableCell sx={{ fontFamily: "monospace", fontSize: "0.85rem" }}>
                                    {row.asin}
                                </TableCell>
                                <TableCell>{row.title}</TableCell>
                                <TableCell align="right">{formatInt(row.bsr)}</TableCell>
                                <TableCell align="right">
                                    {formatPercent(row.buyBoxWinRate30d)}
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>
                                    {formatCurrency0(row.modeledMonthlyRevenue)}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}

const CELL_HEAD = {
    color: "common.white",
    fontWeight: 600,
    fontSize: "0.75rem",
    textTransform: "uppercase",
    letterSpacing: 0.5,
};
